// src/types/Post.ts

import { z } from 'zod';

import { UserProfileSchema } from '@/types/UserProfile';

export enum PostStatus {
    PENDING = 'pending',
    PROCESSING = 'processing',
    VIDEO_PROCESSED = 'video_processed',
    UNPUBLISHED = 'unpublished',
    PUBLISHED = 'published',
    ACTIVE = 'active',
    INACTIVE = 'inactive', // Here as a hack when we take videos down
    SCORED = 'scored',
    FAILED = 'failed',
}
const PostStatusEnum = z.nativeEnum(PostStatus);

export const PostSchema = z.object({
    PK: z.string(),
    SK: z.string(),
    user_id: z.string(),
    content_type: z.literal('post'), // ContentType enum equivalent
    title: z.string(),
    description: z.string().optional().nullable(),

    objectFit: z.string().optional().default('contain'), // Default to 'contain' for video player

    video: z.string().nullable().optional(),
    thumbnail: z.string().nullable().optional(),
    thumbnail_alternates: z.array(z.string()).optional(),
    video_status: PostStatusEnum,

    tags: z.array(z.string()).default(['general']),
    like_count: z.number().default(0),
    comment_count: z.number().default(0),
    view_count: z.number().default(0),
    trending_score: z.number().default(1),

    status: z
        .union([z.literal('unpublished'), z.literal('published'), z.literal('active')])
        .default('unpublished'),

    created_at: z.string().optional(), // ISO format datetime
    updated_at: z.string().optional(),
    scheduled_publish_date: z.string().optional().nullable(),

    // Frontend-only extras
    profile: UserProfileSchema.optional().nullable(),
    user_username: z.string().optional().nullable(),
    user_profile_photo_url: z.string().optional().nullable(),
});

export type Post = z.infer<typeof PostSchema>;

export const now = () =>
    new Date()
        .toISOString()
        .replace('Z', '+0000')
        .replace(/\.\d+/, (m) => (m + '000').slice(0, 7));

export function createPlaceholderPost(postId: string, userId: string): Post {
    return PostSchema.parse({
        PK: postId,
        SK: postId,
        user_id: userId,
        content_type: 'post',
        title: '',
        description: '',
        video_status: 'pending',
        thumbnail: '',
        thumbnail_alternates: [],
        tags: ['general'],
        like_count: 0,
        comment_count: 0,
        view_count: 0,
        trending_score: 1,
        status: 'unpublished',
        created_at: now(),
        updated_at: now(),
    });
}
